import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { getProfile, updateProfile, uploadAvatar, changePassword, sendNotificationEmail, updateUser } from "../../lib/api";
import { Camera, Save, Lock, Check, Trash2, Loader2, User, Mail, Shield, Eye, EyeOff } from "lucide-react";
import { TOAST_NO_PERMISSION, TOAST_PROFILE_UPDATED, TOAST_PROFILE_ERROR, TOAST_PHOTO_UPDATED, TOAST_PHOTO_ERROR, TOAST_PASSWORD_MISMATCH, TOAST_PASSWORD_MIN, TOAST_PASSWORD_CHANGED, TOAST_PASSWORD_ERROR, TOAST_REMOVED, PLH_PUBLIC_NAME, PLH_USER_NAME, PLH_EMAIL, TIP_REMOVE_PHOTO } from "../../lib/constants";
import { logger } from "../../lib/logger";

export default function AdminProfile({ readOnly = false }) {
  const { user, refreshProfile, updateUserState } = useAuth();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [accountName, setAccountName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);
  const [pwdDone, setPwdDone] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  useEffect(() => {
    if (!user) return;
    logger.info("[Profile] User ID:", user.uid);
    setEmail(user.email || "");
    setAccountName(user.name || "");

    getProfile(user.uid).then((p) => {
      logger.info("[Profile] Profile result:", p);
      if (p) {
        setProfile(p);
        setDisplayName(p.display_name || "");
        setAvatarUrl(p.avatar_url || "");
      }
    }).catch((err) => {
      logger.error("Error cargando perfil:", err);
    });
  }, [user]);

  async function handleSave() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    setSaving(true);
    try {
      const updatedUser = await updateUser(user.uid, { email: email.trim(), name: accountName.trim() });
      updateUserState(updatedUser);
      const updatedProfile = await updateProfile(user.uid, { display_name: displayName.trim() });
      setProfile(updatedProfile);
      await refreshProfile();
      toast.success(TOAST_PROFILE_UPDATED);
    } catch (err) {
      logger.error("Failed to save profile/user:", err);
      toast.error(TOAST_PROFILE_ERROR);
    } finally {
      setSaving(false);
    }
  }

  async function handleAvatar(e) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadAvatar(file);
      const updated = await updateProfile(user.uid, { avatar_url: url });
      setAvatarUrl(url);
      setProfile(updated);
      await refreshProfile();
      toast.success(TOAST_PHOTO_UPDATED);
    } catch (err) {
      logger.error("Failed to upload avatar:", err);
      toast.error(TOAST_PHOTO_ERROR);
    } finally {
      setUploading(false);
    }
  }

  async function handleRemoveAvatar() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!avatarUrl) return;
    try {
      const updated = await updateProfile(user.uid, { avatar_url: "" });
      setAvatarUrl("");
      setProfile(updated);
      await refreshProfile();
      toast.success(TOAST_REMOVED);
    } catch (err) {
      logger.error("Failed to remove avatar:", err);
      toast.error(TOAST_PHOTO_ERROR);
    }
  }

  async function handleChangePassword(e) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    e.preventDefault();
    if (pwdNew !== pwdConfirm) return toast.error(TOAST_PASSWORD_MISMATCH);
    if (pwdNew.length < 6) return toast.error(TOAST_PASSWORD_MIN);
    setChangingPwd(true);
    setPwdDone(false);
    try {
      await changePassword(user.uid, pwdCurrent, pwdNew);
      await sendNotificationEmail(
        user.email,
        "Hub Profissional — Palavra-passe alterada",
        `<p>A sua palavra-passe foi alterada com sucesso.</p><p>Se não foi você, responda a este email imediatamente.</p>`
      );
      setPwdDone(true);
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
      toast.success(TOAST_PASSWORD_CHANGED);
    } catch (err) {
      logger.error(err);
      toast.error(err.message || TOAST_PASSWORD_ERROR);
    } finally {
      setChangingPwd(false);
    }
  }

  return (
    <div data-id="admin-profile" data-testid="admin-profile-tab" className="max-w-5xl mx-auto space-y-8">
        {!profile && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-2">
            <p className="text-sm text-amber-800 font-medium">Perfil ainda não configurado.</p>
            <p className="text-xs text-amber-700 mt-1">Preencha os dados abaixo e clique em Guardar para criar o seu perfil.</p>
          </div>
        )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-4xl text-softblack">Meu Perfil</h2>
          <p className="text-warmgray text-sm mt-1">Gerencie suas informações e preferências</p>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Sidebar - Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-fendi rounded-xl p-6 sticky top-24 shadow-sm">
            {/* Avatar */}
            <div className="text-center mb-6">
              <div className="relative w-28 h-28 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-champagne/20 to-fendi/30 border-2 border-fendi/40 group">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={40} className="text-warmgray/40" strokeWidth={1} />
                  </div>
                )}
                {!readOnly && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white transition-all duration-300 rounded-full z-10">
                    {uploading ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <>
                        {avatarUrl && (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveAvatar(); }}
                            className="mb-1 hover:text-red-400 transition-colors"
                            title={TIP_REMOVE_PHOTO}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                        <label className="cursor-pointer flex items-center justify-center">
                          <Camera size={18} />
                          <input type="file" accept="image/*" className="hidden" onChange={handleAvatar} disabled={uploading} />
                        </label>
                      </>
                    )}
                  </div>
                )}
              </div>
              <h3 className="font-serif text-xl text-softblack mt-4">{displayName || "Sem nome"}</h3>
              <p className="text-warmgray text-xs mt-1">{user?.email}</p>
            </div>

            {/* Quick Info */}
            <div className="space-y-3 pt-4 border-t border-fendi/50">
              <div className="flex items-center gap-2 text-sm text-warmgray">
                <User size={14} strokeWidth={1.5} className="text-champagne" />
                <span className="truncate">{accountName || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-warmgray">
                <Mail size={14} strokeWidth={1.5} className="text-champagne" />
                <span className="truncate">{email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Info Card */}
          <div className="bg-white border border-fendi rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center">
                <User size={18} className="text-champagne" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-softblack">Informações Pessoais</h3>
                <p className="text-xs text-warmgray">Seus dados de identificação</p>
              </div>
            </div>

            <fieldset disabled={readOnly} className="space-y-5">
              <label className="block">
                <span className="eyebrow text-xs">Nome da conta</span>
                <div className="relative mt-2">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                  <input
                    className="editorial-field pl-9"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder={PLH_USER_NAME}
                  />
                </div>
              </label>

              <label className="block">
                <span className="eyebrow text-xs">E-mail</span>
                <div className="relative mt-2">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                  <input
                    type="email"
                    className="editorial-field pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={PLH_EMAIL}
                  />
                </div>
              </label>

              <label className="block">
                <span className="eyebrow text-xs">Nome de exibição</span>
                <div className="relative mt-2">
                  <Shield size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                  <input
                    data-testid="profile-input-name"
                    className="editorial-field pl-9"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={PLH_PUBLIC_NAME}
                  />
                </div>
              </label>
            </fieldset>

            {!readOnly && (
              <button onClick={handleSave} disabled={saving} className="btn-primary w-full mt-6" data-testid="profile-save">
                {saving ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <Save size={14} strokeWidth={1.5} className="mr-2" />
                )}
              {saving ? "A guardar..." : "Guardar Alterações"}
            </button>
            )}
          </div>

          {/* Security Card */}
          <div className="bg-white border border-fendi rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center">
                <Lock size={18} className="text-champagne" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-softblack">Segurança</h3>
                <p className="text-xs text-warmgray">Alterar palavra-passe de acesso</p>
              </div>
            </div>

            {pwdDone ? (
              <div className="flex items-start gap-3 bg-green-50/50 border border-green-200/60 rounded-lg p-4">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={16} className="text-green-600" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-medium text-sm text-softblack">Palavra-passe alterada com sucesso</p>
                  <p className="text-xs text-warmgray mt-0.5">Email de confirmação enviado para {user?.email}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-5">
                <label className="block">
                    <span className="eyebrow text-xs">Palavra-passe atual</span>
                  <div className="relative mt-2">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                    <input
                      type={showCurrentPwd ? "text" : "password"}
                      data-testid="pwd-current"
                      className="editorial-field pl-9 pr-10"
                      value={pwdCurrent}
                      onChange={(e) => setPwdCurrent(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warmgray/60 hover:text-softblack transition-colors"
                    >
                      {showCurrentPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </label>

                <label className="block">
                    <span className="eyebrow text-xs">Nova palavra-passe</span>
                  <div className="relative mt-2">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                    <input
                      type={showNewPwd ? "text" : "password"}
                      data-testid="pwd-new"
                      className="editorial-field pl-9 pr-10"
                      value={pwdNew}
                      onChange={(e) => setPwdNew(e.target.value)}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPwd(!showNewPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warmgray/60 hover:text-softblack transition-colors"
                    >
                      {showNewPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </label>

                <label className="block">
                    <span className="eyebrow text-xs">Confirmar nova palavra-passe</span>
                  <div className="relative mt-2">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                    <input
                      type={showConfirmPwd ? "text" : "password"}
                      data-testid="pwd-confirm"
                      className="editorial-field pl-9 pr-10"
                      value={pwdConfirm}
                      onChange={(e) => setPwdConfirm(e.target.value)}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warmgray/60 hover:text-softblack transition-colors"
                    >
                      {showConfirmPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </label>

                <button type="submit" disabled={changingPwd} className="btn-primary w-full" data-testid="pwd-submit">
                  {changingPwd ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <Lock size={14} strokeWidth={1.5} className="mr-2" />
                  )}
                  {changingPwd ? "Alterando..." : "Alterar Palavra-passe"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
